// src/pages/admin/LaboratoryDetailPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import {
    ArrowLeft,
    Info,
    Package,
    Tag,
    Users,
    FileText,
    PencilLine,
    Trash2,
    RefreshCw,
    Megaphone,
    BarChart3,
    Wallet,
    Eye
} from 'lucide-react';
import { toast } from 'react-toastify';

import GeneralTab from '../../components/laboratory/tabs/GeneralTab';
import ProductsTab from '../../components/laboratory/tabs/ProductsTab';
import PromotionsTab from '../../components/laboratory/tabs/PromotionsTab';
import UsersTab from '../../components/laboratory/tabs/UsersTab';
import ContractsTab from '../../components/laboratory/tabs/ContractsTab';
import AdsTab from '../../components/laboratory/tabs/AdsTab';
import LaboratoryStatsTab from '../../components/laboratory/tabs/LaboratoryStatsTab';
import CommissionTab from '../../components/laboratory/tabs/CommissionTab';

import ProductForm from '../../components/laboratory/forms/ProductForm';
import PromotionForm from '../../components/laboratory/forms/PromotionForm';
import ContractForm from '../../components/laboratory/forms/ContractForm';
import UserForm from '../../components/laboratory/forms/UserForm';
import LaboratoryForm from '../../components/laboratory/forms/LaboratoryForm';
import AdForm from '../../components/laboratory/forms/AdForm';

import ConfirmDeleteModal from '../../components/laboratory/modals/ConfirmDeleteModal';

import {
    GET_LABORATORIES,
    GET_USERS_BY_TYPE,
    GET_PRODUCTS_BY_LABORATORY,
    GET_PROMOTIONS_BY_LABORATORY,
    GET_CONTRACTS_BY_LABORATORY,
    GET_ADDS_BY_LABORATORY
} from '../../api/laboratory-api';

const LaboratoryDetailPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Tab state
    const [currentTab, setCurrentTab] = useState('general');

    // Data states
    const [laboratoryProducts, setLaboratoryProducts] = useState([]);
    const [laboratoryPromotions, setLaboratoryPromotions] = useState([]);
    const [laboratoryContracts, setLaboratoryContracts] = useState([]);
    const [laboratoryUsers, setLaboratoryUsers] = useState([]);
    const [laboratoryAdds, setLaboratoryAdds] = useState([]);

    // Selected item states
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [selectedPromotion, setSelectedPromotion] = useState(null);
    const [selectedContract, setSelectedContract] = useState(null);
    const [selectedUser, setSelectedUser] = useState(null);

    // Modal visibility states
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isProductModalOpen, setIsProductModalOpen] = useState(false);
    const [isPromotionModalOpen, setIsPromotionModalOpen] = useState(false);
    const [isContractModalOpen, setIsContractModalOpen] = useState(false);
    const [isUserFormOpen, setIsUserFormOpen] = useState(false);
    const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
    const [isConfirmDeleteUserOpen, setIsConfirmDeleteUserOpen] = useState(false);
    const [isConfirmDeleteProductOpen, setIsConfirmDeleteProductOpen] = useState(false);
    const [isConfirmDeletePromotionOpen, setIsConfirmDeletePromotionOpen] = useState(false);
    const [isConfirmDeleteContractOpen, setIsConfirmDeleteContractOpen] = useState(false);
    const [isAdModalOpen, setIsAdModalOpen] = useState(false);
    const [selectedAd, setSelectedAd] = useState(null);
    const [isConfirmDeleteAdOpen, setIsConfirmDeleteAdOpen] = useState(false);

    // Fetch laboratory data
    const {
        loading: laboratoryLoading,
        error: laboratoryError,
        data: laboratoriesData,
        refetch: refetchLaboratories
    } = useQuery(GET_LABORATORIES, {
        fetchPolicy: 'network-only'
    });

    // Fetch users data
    const {
        data: usersData,
        refetch: refetchUsers
    } = useQuery(GET_USERS_BY_TYPE, {
        variables: { userType: "LABORATORY" },
        fetchPolicy: 'network-only'
    });

    // Lazy queries for details
    const [getProducts, { loading: productsLoading }] = useLazyQuery(GET_PRODUCTS_BY_LABORATORY, {
        fetchPolicy: 'network-only'
    });
    const [getPromotions, { loading: promotionsLoading }] = useLazyQuery(GET_PROMOTIONS_BY_LABORATORY, {
        fetchPolicy: 'network-only'
    });
    const [getContracts, { loading: contractsLoading }] = useLazyQuery(GET_CONTRACTS_BY_LABORATORY, {
        fetchPolicy: 'network-only'
    });

    const [getAdds, { loading: addsLoading }] = useLazyQuery(GET_ADDS_BY_LABORATORY, {
        fetchPolicy: 'network-only'
    });

    // Find current laboratory
    const laboratory = laboratoriesData?.laboratories?.find(lab => lab.id === id);

    // Load laboratory details
    useEffect(() => {
        if (id) {
            loadLaboratoryDetails(id);
        }
    }, [id]);

    // Set laboratory users when laboratory data changes
    useEffect(() => {
        if (laboratory && laboratory.users) {
            setLaboratoryUsers(laboratory.users);
        }
    }, [laboratory]);

    // Function to load laboratory details
    const loadLaboratoryDetails = async (laboratoryId) => {
        try {
            // Fetch products
            const productsResult = await getProducts({
                variables: { laboratoryId }
            });

            if (productsResult.data && productsResult.data.productsByLaboratory) {
                setLaboratoryProducts(productsResult.data.productsByLaboratory);
            }

            // Fetch promotions
            const promotionsResult = await getPromotions({
                variables: { laboratoryId }
            });

            if (promotionsResult.data && promotionsResult.data.promotionsByLaboratory) {
                setLaboratoryPromotions(promotionsResult.data.promotionsByLaboratory);
            }

            // Fetch contracts
            const contractsResult = await getContracts({
                variables: { laboratoryId }
            });

            if (contractsResult.data && contractsResult.data.contractsByLaboratory) {
                setLaboratoryContracts(contractsResult.data.contractsByLaboratory);
            }

            // Fetch adds
            const addsResult = await getAdds({
                variables: { laboratoryId }
            });

            if (addsResult.data && addsResult.data.addsByLaboratory) {
                setLaboratoryAdds(addsResult.data.addsByLaboratory);
            }
        } catch (error) {
            console.error('Error loading laboratory details:', error);
            toast.error('Erreur lors du chargement des détails du laboratoire');
        }
    };

    // Handle refresh button click
    const handleRefresh = () => {
        if (id) {
            loadLaboratoryDetails(id);
            refetchLaboratories();
            toast.success('Données actualisées');
        }
    };

    // Handle edit button click
    const handleEditClick = () => {
        setIsEditModalOpen(true);
    };

    // Handle delete button click
    const handleDeleteClick = () => {
        setIsConfirmDeleteOpen(true);
    };

    // Handle navigating back to labs list
    const handleBackToList = () => {
        navigate('/admin/laboratories');
    };

    const handleViewAsLab = () => {
        const owner = laboratory.owner;
        if (!owner) {
            toast.error("Ce laboratoire n'a pas de propriétaire");
            return;
        }
        sessionStorage.setItem('adminSession', JSON.stringify({
            authToken: localStorage.getItem('authToken'),
            userType: localStorage.getItem('userType'),
            userData: localStorage.getItem('userData'),
            labName: laboratory.nom,
            labId: laboratory.id,
        }));
        localStorage.setItem('userType', 'LABORATORY');
        localStorage.setItem('userData', JSON.stringify({
            id: owner.id,
            email: owner.email,
            firstName: owner.firstName,
            lastName: owner.lastName,
            userType: 'LABORATORY',
            isActive: true,
        }));
        navigate('/laboratory/dashboard');
    };

    // Product handlers
    const handleAddProduct = () => {
        setSelectedProduct(null);
        setIsProductModalOpen(true);
    };

    const handleEditProduct = (product) => {
        setSelectedProduct(product);
        setIsProductModalOpen(true);
    };

    const handleDeleteProduct = (product) => {
        setSelectedProduct(product);
        setIsConfirmDeleteProductOpen(true);
    };

    const handleProductSuccess = () => {
        setIsProductModalOpen(false);
        loadLaboratoryDetails(id);
    };

    // Promotion handlers
    const handleAddPromotion = () => {
        setSelectedPromotion(null);
        setIsPromotionModalOpen(true);
    };

    const handleEditPromotion = (promotion) => {
        setSelectedPromotion(promotion);
        setIsPromotionModalOpen(true);
    };

    const handleDeletePromotion = (promotion) => {
        setSelectedPromotion(promotion);
        setIsConfirmDeletePromotionOpen(true);
    };

    const handlePromotionSuccess = () => {
        setIsPromotionModalOpen(false);
        loadLaboratoryDetails(id);
    };

    // Contract handlers
    const handleAddContract = () => {
        setSelectedContract(null);
        setIsContractModalOpen(true);
    };

    const handleEditContract = (contract) => {
        setSelectedContract(contract);
        setIsContractModalOpen(true);
    };

    const handleDeleteContract = (contract) => {
        setSelectedContract(contract);
        setIsConfirmDeleteContractOpen(true);
    };

    const handleContractSuccess = () => {
        setIsContractModalOpen(false);
        loadLaboratoryDetails(id);
    };

    // User handlers
    const handleAddUser = () => {
        setIsUserFormOpen(true);
    };

    const handleDeleteUser = (user) => {
        setSelectedUser(user);
        setIsConfirmDeleteUserOpen(true);
    };

    const handleUserSuccess = () => {
        setIsUserFormOpen(false);
        // Refetch users data
        refetchLaboratories();
        refetchUsers();
        loadLaboratoryDetails(id);
    };

    // Laboratory edit success handler
    const handleLaboratoryEditSuccess = () => {
        setIsEditModalOpen(false);
        refetchLaboratories();
        toast.success('Laboratoire mis à jour avec succès');
    };

    // Laboratory delete success handler
    const handleLaboratoryDeleteSuccess = () => {
        toast.success('Laboratoire supprimé avec succès');
        navigate('/admin/laboratories');
    };

    const handleAddAd = () => {
        setSelectedAd(null);
        setIsAdModalOpen(true);
    };

    const handleEditAd = (ad) => {
        setSelectedAd(ad);
        setIsAdModalOpen(true);
    };

    const handleDeleteAd = (ad) => {
        setSelectedAd(ad);
        setIsConfirmDeleteAdOpen(true);
    };

    const handleAdSuccess = () => {
        setIsAdModalOpen(false);
        loadLaboratoryDetails(id);
    };

    // Render tab content
    const renderTabContent = () => {
        if (!laboratory) return <div>Chargement...</div>;

        switch (currentTab) {
            case 'general':
                return (
                    <GeneralTab
                        laboratory={laboratory}
                        productsCount={laboratoryProducts.length}
                        promotionsCount={laboratoryPromotions.length}
                        refetch={refetchLaboratories}
                    />
                );
            case 'products':
                return (
                    <ProductsTab
                        products={laboratoryProducts}
                        loading={productsLoading}
                        onAddProduct={handleAddProduct}
                        onEditProduct={handleEditProduct}
                        onDeleteProduct={handleDeleteProduct}
                        promotions={laboratoryPromotions}
                        laboratory={laboratory} // Add laboratory prop
                        adds={laboratoryAdds} // Add adds prop
                    />
                );
            case 'promotions':
                return (
                    <PromotionsTab
                        promotions={laboratoryPromotions}
                        loading={promotionsLoading}
                        laboratory={laboratory}
                        onAddPromotion={handleAddPromotion}
                        onEditPromotion={handleEditPromotion}
                        onDeletePromotion={handleDeletePromotion}
                        refetchPromotions={() => loadLaboratoryDetails(id)} // Pass refetch function
                    />
                );
            case 'users':
                return (
                    <UsersTab
                    laboratory={laboratory}
                        users={laboratoryUsers}
                        owner={laboratory.user}
                        onAddUser={handleAddUser}
                        onRemoveUser={handleDeleteUser}
                    />
                );
            case 'contrats':
                return (
                    <ContractsTab
                        contracts={laboratoryContracts}
                        loading={contractsLoading}
                        onAddContract={handleAddContract}
                        onEditContract={handleEditContract}
                        onDeleteContract={handleDeleteContract}
                    />
                );
            case 'ads':
                return (
                    <AdsTab
                        adds={laboratoryAdds}
                        loading={addsLoading}
                        onAddAd={handleAddAd}
                        onEditAd={handleEditAd}
                        onDeleteAd={handleDeleteAd}
                        laboratory={laboratory}
                        products={laboratoryProducts}
                        refetchAds={() => loadLaboratoryDetails(id)}
                    />
                );
            case 'stats':
                return (
                    <LaboratoryStatsTab
                        laboratory={laboratory}
                        promotions={laboratoryPromotions}
                        products={laboratoryProducts}
                        users={laboratoryUsers}
                        contracts={laboratoryContracts}
                        adds={laboratoryAdds}
                    />
                );
            case 'commission':
                return (
                    <CommissionTab
                        laboratory={laboratory}
                        refetch={refetchLaboratories}
                    />
                );
            default:
                return <div>Select a tab</div>;
        }
    };

    if (laboratoryLoading && !laboratory) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="spinner-border text-green-500" role="status">
                    <span className="sr-only">Chargement...</span>
                </div>
            </div>
        );
    }

    if (laboratoryError) {
        return (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Erreur!</strong>
                <span className="block sm:inline"> Impossible de charger les données: {laboratoryError.message}</span>
            </div>
        );
    }

    if (!laboratory) {
        return (
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
                <strong className="font-bold">Attention!</strong>
                <span className="block sm:inline"> Laboratoire non trouvé.</span>
                <button
                    onClick={handleBackToList}
                    className="mt-3 bg-yellow-500 hover:bg-yellow-700 text-white font-bold py-2 px-4 rounded"
                >
                    Retour à la liste
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Back button and title */}
            <div className="flex items-center gap-3">
                <button
                    onClick={handleBackToList}
                    className="p-2 rounded-full hover:bg-gray-200 flex-shrink-0"
                    title="Retour à la liste"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="text-xl sm:text-2xl font-bold truncate">{laboratory.nom}</h1>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-2 sm:gap-3">
                <button
                    onClick={handleEditClick}
                    className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
                >
                    <PencilLine className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
                    Modifier
                </button>

                <button
                    onClick={handleDeleteClick}
                    className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700"
                >
                    <Trash2 className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
                    Supprimer
                </button>

                <button
                    onClick={handleRefresh}
                    className="inline-flex items-center px-3 sm:px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                    <RefreshCw className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
                    <span className="hidden sm:inline">Actualiser</span>
                </button>

                <button
                    onClick={handleViewAsLab}
                    className="inline-flex items-center px-3 sm:px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-500 hover:bg-amber-600"
                >
                    <Eye className="-ml-0.5 mr-1.5 h-4 w-4" aria-hidden="true" />
                    Vue labo
                </button>
            </div>

            {/* Tabs */}
            <div className="bg-white shadow rounded-lg">
                <div className="border-b border-gray-200 overflow-x-auto">
                    <nav className="-mb-px flex space-x-4 sm:space-x-8 px-4 min-w-max">
                        <button
                            onClick={() => setCurrentTab('general')}
                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'general'
                                ? 'border-green-500 text-green-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <Info className="inline-block mr-1 h-5 w-5" />
                            Général
                        </button>
                        <button
                            onClick={() => setCurrentTab('products')}
                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'products'
                                ? 'border-green-500 text-green-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <Package className="inline-block mr-1 h-5 w-5" />
                            Produits
                        </button>
                        <button
                            onClick={() => setCurrentTab('promotions')}
                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'promotions'
                                ? 'border-green-500 text-green-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <Tag className="inline-block mr-1 h-5 w-5" />
                            Promotions
                        </button>
                        <button
                            onClick={() => setCurrentTab('users')}
                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'users'
                                ? 'border-green-500 text-green-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <Users className="inline-block mr-1 h-5 w-5" />
                            Utilisateurs
                        </button>
                        <button
                            onClick={() => setCurrentTab('contrats')}
                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'contrats'
                                ? 'border-green-500 text-green-600'
                                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <FileText className="inline-block mr-1 h-5 w-5" />
                            Contrats
                        </button>
                        <button
                            onClick={() => setCurrentTab('ads')}
                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'ads'
                                    ? 'border-green-500 text-green-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <Megaphone className="inline-block mr-1 h-5 w-5" />
                            Publicités
                        </button>
                        <button
                            onClick={() => setCurrentTab('stats')}
                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'stats'
                                    ? 'border-green-500 text-green-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <BarChart3 className="inline-block mr-1 h-5 w-5" />
                            Statistiques
                        </button>
                        <button
                            onClick={() => setCurrentTab('commission')}
                            className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${currentTab === 'commission'
                                    ? 'border-green-500 text-green-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                }`}
                        >
                            <Wallet className="inline-block mr-1 h-5 w-5" />
                            Dotation
                        </button>
                    </nav>
                </div>

                {/* Tab Content */}
                <div className="p-6">
                    {renderTabContent()}
                </div>
            </div>

            {/* Laboratory Edit Modal */}
            {isEditModalOpen && (
                <LaboratoryForm
                    laboratory={laboratory}
                    isOpen={isEditModalOpen}
                    onClose={() => setIsEditModalOpen(false)}
                    onSuccess={handleLaboratoryEditSuccess}
                    users={usersData?.usersByType || []}
                />
            )}

            {/* Product Form Modal */}
            {isProductModalOpen && (
                <ProductForm
                    laboratory={laboratory}
                    product={selectedProduct}
                    isOpen={isProductModalOpen}
                    onClose={() => setIsProductModalOpen(false)}
                    onSuccess={handleProductSuccess}
                />
            )}

            {/* Promotion Form Modal */}
            {isPromotionModalOpen && (
                <PromotionForm
                    laboratory={laboratory}
                    promotion={selectedPromotion}
                    isOpen={isPromotionModalOpen}
                    onClose={() => setIsPromotionModalOpen(false)}
                    onSuccess={handlePromotionSuccess}
                />
            )}

            {/* Contract Form Modal */}
            {isContractModalOpen && (
                <ContractForm
                    laboratory={laboratory}
                    contract={selectedContract}
                    isOpen={isContractModalOpen}
                    onClose={() => setIsContractModalOpen(false)}
                    onSuccess={handleContractSuccess}
                />
            )}

            {/* User Form Modal */}
            {isUserFormOpen && (
                <UserForm
                    laboratory={laboratory}
                    isOpen={isUserFormOpen}
                    onClose={() => setIsUserFormOpen(false)}
                    onSuccess={handleUserSuccess}
                />
            )}

            {/* Confirm Delete Laboratory Modal */}
            {isConfirmDeleteOpen && (
                <ConfirmDeleteModal
                    laboratory={laboratory}
                    isOpen={isConfirmDeleteOpen}
                    onClose={() => setIsConfirmDeleteOpen(false)}
                    onConfirm={handleLaboratoryDeleteSuccess}
                />
            )}

            {/* Confirm Delete User Modal */}
            {isConfirmDeleteUserOpen && selectedUser && (
                <ConfirmDeleteModal
                    type="user"
                    item={selectedUser}
                    laboratory={laboratory}
                    isOpen={isConfirmDeleteUserOpen}
                    onClose={() => setIsConfirmDeleteUserOpen(false)}
                    onConfirm={() => {
                        // Handle user deletion
                        setIsConfirmDeleteUserOpen(false);
                        loadLaboratoryDetails(id);
                        refetchLaboratories();
                        refetchUsers();
                    }}
                />
            )}

            {/* Confirm Delete Product Modal */}
            {isConfirmDeleteProductOpen && selectedProduct && (
                <ConfirmDeleteModal
                    type="product"
                    item={selectedProduct}
                    laboratory={laboratory}
                    isOpen={isConfirmDeleteProductOpen}
                    onClose={() => setIsConfirmDeleteProductOpen(false)}
                    onConfirm={() => {
                        // Handle product deletion
                        setIsConfirmDeleteProductOpen(false);
                        loadLaboratoryDetails(id);
                    }}
                />
            )}

            {/* Confirm Delete Promotion Modal */}
            {isConfirmDeletePromotionOpen && selectedPromotion && (
                <ConfirmDeleteModal
                    type="promotion"
                    item={selectedPromotion}
                    laboratory={laboratory}
                    isOpen={isConfirmDeletePromotionOpen}
                    onClose={() => setIsConfirmDeletePromotionOpen(false)}
                    onConfirm={() => {
                        // Handle promotion deletion
                        setIsConfirmDeletePromotionOpen(false);
                        loadLaboratoryDetails(id);
                    }}
                />
            )}

            {/* Confirm Delete Contract Modal */}
            {isConfirmDeleteContractOpen && selectedContract && (
                <ConfirmDeleteModal
                    type="contract"
                    item={selectedContract}
                    laboratory={laboratory}
                    isOpen={isConfirmDeleteContractOpen}
                    onClose={() => setIsConfirmDeleteContractOpen(false)}
                    onConfirm={() => {
                        // Handle contract deletion
                        setIsConfirmDeleteContractOpen(false);
                        loadLaboratoryDetails(id);
                    }}
                />
            )}

            {/* Ad Form Modal */}
            {isAdModalOpen && (
                <AdForm
                    laboratory={laboratory}
                    ad={selectedAd}
                    isOpen={isAdModalOpen}
                    onClose={() => setIsAdModalOpen(false)}
                    onSuccess={handleAdSuccess}
                    products={laboratoryProducts}
                />
            )}

            {/* Confirm Delete Ad Modal */}
            {isConfirmDeleteAdOpen && selectedAd && (
                <ConfirmDeleteModal
                    type="ad"
                    item={selectedAd}
                    laboratory={laboratory}
                    isOpen={isConfirmDeleteAdOpen}
                    onClose={() => setIsConfirmDeleteAdOpen(false)}
                    onConfirm={() => {
                        // Handle ad deletion
                        setIsConfirmDeleteAdOpen(false);
                        loadLaboratoryDetails(id);
                    }}
                />
            )}
        </div>
    );
};

export default LaboratoryDetailPage;